import React, { useEffect, useState } from 'react';
import { ArrowLeft, Trash2, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import LogoLoader from '../components/LogoLoader';
import toast from 'react-hot-toast';

const ALLOWED_EMAIL = 'alameengoldandsilver@gmail.com';

const s = {
  page: { height: '100vh', background: '#faf7f0', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  navbar: {
    background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)',
    padding: '0 20px', height: '64px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    boxShadow: '0 2px 20px rgba(0,0,0,0.3)',
    flexShrink: 0,
  },
  navBrand: { display: 'flex', alignItems: 'center', gap: '10px' },
  navCoin: {
    width: '42px', height: '42px',
    borderRadius: '50%', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  navTitle: { color: '#fff', fontSize: '18px', fontWeight: '800', margin: 0 },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.1)', color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.15)',
    padding: '8px 14px', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  content: { padding: '32px 24px' },
  headerCard: {
    background: '#fff', borderRadius: '16px', padding: '20px 28px',
    marginBottom: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
    display: 'flex', alignItems: 'center', gap: '14px',
  },
  iconBox: {
    width: '46px', height: '46px',
    background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
    borderRadius: '12px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', flexShrink: 0,
  },
  headerTitle: { fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 },
  headerSub: { fontSize: '13px', color: '#64748b', margin: '3px 0 0' },
  tableCard: { background: '#fff', borderRadius: '16px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', overflow: 'hidden' },
  filterRow: {
    display: 'flex', gap: '8px', padding: '16px 20px',
    borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', alignItems: 'center',
  },
  filterBtn: (active) => ({
    padding: '6px 16px', borderRadius: '20px', border: 'none',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    background: active ? '#C9A84C' : '#f1f5f9',
    color: active ? '#fff' : '#64748b',
  }),
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '11px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', background: '#f8fafc', letterSpacing: '0.5px', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' },
  td: { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap' },
  typeBadge: (type) => ({
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700',
    background: type === 'users' ? '#fef3c7' : '#fdf5dc',
    color: type === 'users' ? '#D97706' : '#9A7A2C',
  }),
  emptyState: {
    textAlign: 'center', padding: '60px 20px',
    color: '#94a3b8', fontSize: '15px',
  },
  delBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca',
    borderRadius: '8px', padding: '5px 10px', fontSize: '12px',
    fontWeight: '700', cursor: 'pointer', gap: '4px',
  },
};

export default function DeleteHistory() {
  const navigate = useNavigate();
  const [deletedAmounts, setDeletedAmounts] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'users' | 'amounts'
  const [selectedPeriod, setSelectedPeriod] = useState(''); // '' | 'this' | 'last' | 'last3'
  const [confirmTarget, setConfirmTarget] = useState(null); // { type, label, onConfirm }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email?.toLowerCase() !== ALLOWED_EMAIL) {
        setAllowed(false);
        setLoading(false);
      } else {
        setAllowed(true);
        const fetchLogs = async () => {
          const [{ data: amtData }, { data: usrData }] = await Promise.all([
            supabase.from('deleted_amounts').select('*').order('deleted_at', { ascending: false }),
            supabase.from('deleted_users').select('*').order('deleted_at', { ascending: false }),
          ]);
          setDeletedAmounts(amtData || []);
          setDeletedUsers(usrData || []);
          setLoading(false);
        };
        fetchLogs();
      }
    });
  }, []);

  const getPeriodMonths = () => {
    const now = new Date();
    if (selectedPeriod === 'this') {
      return [`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`];
    }
    if (selectedPeriod === 'last') {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return [`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`];
    }
    if (selectedPeriod === 'last3') {
      return Array.from({ length: 3 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - 1 - i, 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      });
    }
    return [];
  };
  const periodMonths = getPeriodMonths();

  const matchesMonth = (dateStr, isTs = false) => {
    if (periodMonths.length === 0 || !dateStr) return true;
    const d = isTs ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return periodMonths.includes(ym);
  };

  const showUserSection = filter === 'all' || filter === 'users';
  const showAmountSection = filter === 'all' || filter === 'amounts';
  // Table always shows all records — month only used for bulk removal
  const filteredUserRows = showUserSection ? deletedUsers : [];
  const filteredAmountRows = showAmountSection ? deletedAmounts : [];
  const displayCount = filteredUserRows.length + filteredAmountRows.length;

  // Count for the selected period (used in remove button label only)
  const monthAmtIds = selectedPeriod
    ? deletedAmounts.filter(a => matchesMonth(a.deleted_at, true)).map(a => a.id)
    : [];
  const monthUsrIds = selectedPeriod
    ? deletedUsers.filter(u => matchesMonth(u.deleted_at, true)).map(u => u.id)
    : [];
  const monthCount = monthAmtIds.length + monthUsrIds.length;

  const handleRemoveMonthHistory = () => {
    if (!selectedPeriod || monthCount === 0) return;
    const label = selectedPeriod === 'this' ? 'This Month' : selectedPeriod === 'last' ? 'Last Month' : 'Last 3 Months';
    setConfirmTarget({
      title: 'Remove Period History?',
      message: `Remove all ${monthCount} delete history record${monthCount !== 1 ? 's' : ''} for ${label}?`,
      onConfirm: async () => {
        if (monthAmtIds.length > 0) await supabase.from('deleted_amounts').delete().in('id', monthAmtIds);
        if (monthUsrIds.length > 0) await supabase.from('deleted_users').delete().in('id', monthUsrIds);
        setDeletedAmounts(prev => prev.filter(a => !monthAmtIds.includes(a.id)));
        setDeletedUsers(prev => prev.filter(u => !monthUsrIds.includes(u.id)));
        setSelectedPeriod('');
        setConfirmTarget(null);        toast.success('History removed successfully!');      },
    });
  };

  if (allowed === null || loading) return <LogoLoader />;

  if (allowed === false) return (
    <div style={{ minHeight: '100vh', background: '#faf7f0', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column' }}>
      <nav style={s.navbar}>
        <div style={s.navBrand}>
          <div style={{ ...s.navCoin, cursor: 'pointer' }} onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%' }} />
          </div>
          <p style={s.navTitle}>Al Ameen Gold &amp; Silver</p>
        </div>
        <button onClick={() => navigate('/dashboard')} style={s.backBtn}>
          <ArrowLeft size={15} /> Back
        </button>
      </nav>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ background: '#fff', borderRadius: '20px', padding: '48px 40px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Lock size={34} color="#ef4444" />
          </div>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>Access Denied</h2>
          <p style={{ margin: '0 0 6px', fontSize: '15px', color: '#64748b' }}>You don't have permission to view the Delete History.</p>
          <p style={{ margin: '0 0 28px', fontSize: '13px', color: '#94a3b8' }}>This page is restricted to authorised administrators only.</p>
          <button onClick={() => navigate('/dashboard')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            <ArrowLeft size={16} /> Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const savingTypeBadge = (type) => {
    if (type === 'gold') return <span style={{ display: 'inline-block', background: '#fef3c7', color: '#D97706', borderRadius: '20px', padding: '2px 8px', fontSize: '11px', fontWeight: '700' }}>🥇 Gold</span>;
    if (type === 'silver') return <span style={{ display: 'inline-block', background: '#f1f5f9', color: '#475569', borderRadius: '20px', padding: '2px 8px', fontSize: '11px', fontWeight: '700' }}>🥈 Silver</span>;
    return <span style={{ display: 'inline-block', background: '#dcfce7', color: '#16a34a', borderRadius: '20px', padding: '2px 8px', fontSize: '11px', fontWeight: '700' }}>💰 Money</span>;
  };

  return (
    <div style={s.page}>
      <nav style={s.navbar} className="gs-navbar">
        <div style={s.navBrand}>
          <div style={{ ...s.navCoin, cursor: 'pointer' }} className="gs-nav-coin" onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
          </div>
          <p style={s.navTitle} className="gs-nav-title">Al Ameen Gold & Silver</p>
        </div>
        <button onClick={() => navigate('/admin')} style={s.backBtn}>
          <ArrowLeft size={15} /> Back
        </button>
      </nav>

      <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={s.content} className="gs-content">
        <div style={s.headerCard} className="gs-dh-header">
          <div style={s.iconBox}><Trash2 size={22} /></div>
          <div>
            <p style={s.headerTitle}>Delete History</p>
            <p style={s.headerSub}>Audit log of all deleted members and transactions</p>
          </div>
        </div>

        <div style={s.tableCard} className="gs-dh-table">
          {/* Filters */}
          <div style={s.filterRow} className="gs-dh-filter-row">
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', marginRight: '4px' }}>Filter:</span>
            <button style={s.filterBtn(filter === 'all')} onClick={() => setFilter('all')}>All ({deletedUsers.length + deletedAmounts.length})</button>
            <button style={s.filterBtn(filter === 'users')} onClick={() => setFilter('users')}>👤 Members ({deletedUsers.length})</button>
            <button style={s.filterBtn(filter === 'amounts')} onClick={() => setFilter('amounts')}>💰 Transactions ({deletedAmounts.length})</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px', flexWrap: 'wrap' }}>
              {[{ key: 'this', label: 'This Month' }, { key: 'last', label: 'Last Month' }, { key: 'last3', label: 'Last 3 Months' }].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setSelectedPeriod(selectedPeriod === key ? '' : key)}
                  style={{ padding: '5px 12px', borderRadius: '20px', border: `1.5px solid ${selectedPeriod === key ? '#C9A84C' : '#e2e8f0'}`, fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: selectedPeriod === key ? '#fef9ec' : '#f8fafc', color: selectedPeriod === key ? '#C9A84C' : '#64748b' }}
                >
                  {label}
                </button>
              ))}
              {selectedPeriod && (
                <>
                  <button
                    onClick={handleRemoveMonthHistory}
                    disabled={monthCount === 0}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '8px', border: '1px solid #fecaca', background: monthCount > 0 ? '#fef2f2' : '#f8fafc', color: monthCount > 0 ? '#ef4444' : '#94a3b8', fontSize: '12px', fontWeight: '700', cursor: monthCount > 0 ? 'pointer' : 'not-allowed' }}
                  >
                    <Trash2 size={12} /> Remove {monthCount} record{monthCount !== 1 ? 's' : ''}
                  </button>
                  <button
                    onClick={() => setSelectedPeriod('')}
                    style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                  >✕</button>
                </>
              )}
            </div>
            <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#94a3b8' }}>
              {displayCount} Record{displayCount !== 1 ? 's' : ''}
            </span>
          </div>

          {displayCount === 0 ? (
            <div style={s.emptyState}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>🗑️</div>
              No delete records found.
            </div>
          ) : (
            <>
              {/* Transaction Deletions Section */}
              {showAmountSection && filteredAmountRows.length > 0 && (
                <>
                  {filter === 'all' && (
                    <div style={{ padding: '10px 20px', background: '#fef9ec', borderBottom: '1px solid #f1f5f9', borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        💰 Transaction Deletions ({filteredAmountRows.length})
                      </span>
                    </div>
                  )}
                  <div className="gs-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: filter === 'all' ? '300px' : '520px' }}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>Member</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Customer ID</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Type</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Amount</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Gram</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>GST</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>GST %</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Added Date</th>
                          <th style={s.th}>Note</th>
                          <th style={s.th}>Deleted By</th>
                          <th style={s.th}>Deleted At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAmountRows.map((row, i) => (
                          <tr key={`da-${row.id}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ ...s.td, fontWeight: '600', color: '#0f172a' }}>{row.member_name || '—'}</td>
                            <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>#{row.user_id || '—'}</td>
                            <td style={{ ...s.td, textAlign: 'center' }}>{savingTypeBadge(row.saving_type)}</td>
                            <td style={{ ...s.td, textAlign: 'center' }}>
                              <span style={{ display: 'inline-block', background: '#fef2f2', color: '#ef4444', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>
                                ₹{Number(row.amount || 0).toLocaleString('en-IN')}
                              </span>
                            </td>
                            <td style={{ ...s.td, textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#D97706' }}>
                              {Number(row.gram || 0) > 0 ? `${Number(row.gram).toFixed(3)} g` : '—'}
                            </td>
                            <td style={{ ...s.td, textAlign: 'center' }}>
                              {row.saving_type !== 'money'
                                ? row.gst_applied
                                  ? <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', background: '#dcfce7', color: '#16a34a', borderRadius: '50%', border: '1.5px solid #bbf7d0', fontSize: '11px', fontWeight: '900' }}>✓</span>
                                  : <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', background: '#fef2f2', color: '#ef4444', borderRadius: '50%', border: '1.5px solid #fecaca', fontSize: '11px', fontWeight: '900' }}>✕</span>
                                : <span style={{ color: '#94a3b8' }}>—</span>
                              }
                            </td>
                            <td style={{ ...s.td, textAlign: 'center' }}>
                              {row.saving_type !== 'money'
                                ? row.gst_applied && row.gst_percent
                                  ? <span style={{ display: 'inline-block', background: '#fef9ec', color: '#C9A84C', borderRadius: '20px', padding: '3px 10px', fontWeight: '700', fontSize: '12px', border: '1px solid #f5d87e' }}>{row.gst_percent}%</span>
                                  : <span style={{ display: 'inline-block', background: '#f1f5f9', color: '#64748b', borderRadius: '20px', padding: '3px 10px', fontWeight: '700', fontSize: '12px' }}>0%</span>
                                : <span style={{ color: '#94a3b8' }}>—</span>
                              }
                            </td>
                            <td style={{ ...s.td, color: '#64748b', textAlign: 'center' }}>
                              {row.date ? new Date(row.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </td>
                            <td style={{ ...s.td, color: '#64748b', fontStyle: 'italic' }}>{row.note || '—'}</td>
                            <td style={{ ...s.td, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.deleted_by || '—'}>{row.deleted_by || '—'}</td>
                            <td style={{ ...s.td, color: '#64748b' }}>{formatDate(row.deleted_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Member Deletions Section */}
              {showUserSection && filteredUserRows.length > 0 && (
                <>
                  {filter === 'all' && (
                    <div style={{ padding: '10px 20px', background: '#fef3c7', borderBottom: '1px solid #f1f5f9', borderTop: filteredAmountRows.length > 0 ? '2px solid #e2e8f0' : '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        👤 Member Deletions ({filteredUserRows.length})
                      </span>
                    </div>
                  )}
                  <div className="gs-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: filter === 'all' ? '300px' : '520px' }}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>Member</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Customer ID</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Type</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Phone</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Total Amount</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Total Gram</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Balance</th>
                          <th style={{ ...s.th, textAlign: 'center' }}>Joined</th>
                          <th style={s.th}>Deleted By</th>
                          <th style={s.th}>Deleted At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUserRows.map((row, i) => (
                          <tr key={`du-${row.id}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ ...s.td, fontWeight: '600', color: '#0f172a' }}>{row.name || '—'}</td>
                            <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>#{row.original_id || '—'}</td>
                            <td style={{ ...s.td, textAlign: 'center' }}>{savingTypeBadge(row.saving_type)}</td>
                            <td style={{ ...s.td, textAlign: 'center' }}>{row.phone || '—'}</td>
                            <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', color: '#065f46' }}>
                              ₹{Number(row.total_amount || 0).toLocaleString('en-IN')}
                            </td>
                            <td style={{ ...s.td, textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#D97706' }}>
                              {Number(row.total_gram || 0) > 0 ? `${Number(row.total_gram).toFixed(3)} g` : '—'}
                            </td>
                            <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', color: '#7c3aed' }}>
                              ₹{Number(row.live_balance || 0).toLocaleString('en-IN')}
                            </td>
                            <td style={{ ...s.td, textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                              {row.joined_at ? new Date(row.joined_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </td>
                            <td style={{ ...s.td, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.deleted_by || '—'}>{row.deleted_by || '—'}</td>
                            <td style={{ ...s.td, color: '#64748b' }}>{formatDate(row.deleted_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      </div>

      {/* Custom Confirm Modal */}
      {confirmTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px 28px 24px', width: '100%', maxWidth: '380px', boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Trash2 size={24} color="#ef4444" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>{confirmTarget.title}</h3>
              <p style={{ margin: '0 0 6px', fontSize: '14px', color: '#64748b' }}>{confirmTarget.message}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>This action cannot be undone.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setConfirmTarget(null)}
                style={{ flex: 1, padding: '11px', borderRadius: '9px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '14px', fontWeight: '700', color: '#64748b', cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={confirmTarget.onConfirm}
                style={{ flex: 1, padding: '11px', borderRadius: '9px', border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', fontSize: '14px', fontWeight: '700', color: '#fff', cursor: 'pointer', boxShadow: '0 4px 12px rgba(239,68,68,0.35)' }}
              >Yes, Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
